<template>
  <div id="wrapper" :class="wrapperClass" class="d-flex" :style="wrapperStyle">
    <TopBar :sideBarCollapsed="sideBarCollapsed" :style="modalOpenStyle.topbar" id="topbar"></TopBar>
    <div ref="app" id="app" class="mx-auto d-flex flex-stretch">
      <SideBar
        :collapsed="sideBarCollapsed"
        :windowWidth="windowWidth"
        @toggle-collapse="$store.dispatch('setSideBarCollapsed', !sideBarCollapsed)"
        id="sidebar"
      ></SideBar>
      <div id="main" class="flex-stretch">
        <EmailVerificationAlert v-if="user && !emailVerified"></EmailVerificationAlert>
        <RouterView :key="$route.fullPath"></RouterView>
      </div>
    </div>
    <SignInModal v-if="modals['signInModal']" @close="closeModal('signInModal')" :show-header="false" :show-footer="false"></SignInModal>
    <InfoModal v-if="modals['infoModal']" @close="closeModal('infoModal')" :show-header="true" :show-footer="true"></InfoModal>
    <AccountModal v-if="modals['accountModal']" @close="closeModal('accountModal')" :show-header="true" :show-footer="false"></AccountModal>
    <FeedbackModal v-if="modals['feedbackModal']" @close="closeModal('feedbackModal')" :show-header="true" :show-footer="true"></FeedbackModal>
    <PassphrasePromptModal v-if="promptForPassphrase" :show-header="true" :show-footer="false" :show-close="false"></PassphrasePromptModal>
  </div>
</template>

<script>
import { initializeFirebase } from '@/utils/firebase'
import TopBar from '@/components/TopBar'
import SideBar from '@/components/SideBar'
import EmailVerificationAlert from '@/components/EmailVerificationAlert'
import InfoModal from '@/components/Modals/Info'
import SignInModal from '@/components/Modals/SignIn'
import AccountModal from '@/components/Modals/Account'
import FeedbackModal from '@/components/Modals/Feedback'
import PassphrasePromptModal from '@/components/PassphrasePrompt'
import EventBus from '@/utils/event-bus'
import debounce from 'lodash/debounce'
import { mapState } from 'vuex'
import goToNote from '@/mixins/go-to-note'
import variables from '@/assets/_variables.scss'

export default {
  name: 'app',
  components: {
    TopBar,
    SideBar,
    EmailVerificationAlert,
    InfoModal,
    SignInModal,
    AccountModal,
    FeedbackModal,
    PassphrasePromptModal
  },
  mixins: [goToNote],
  data() {
    return {
      modals: {},
      minHeight: 0,
      windowWidth: 0,
      scrollbarWidth: 0
    }
  },
  computed: {
    emailVerified() {
      return this.user ? this.user.emailVerified : false
    },
    currentNote() {
      return this.$store.getters.currentNote
    },
    promptForPassphrase() {
      return this.$store.getters['encryption/promptForPassphrase']
    },
    modalOpen() {
      return Object.keys(this.modals).reduce((modalOpen, modal) => {
        if (this.modals[modal]) modalOpen = true
        return modalOpen
      }, false)
    },
    /* Only necessary if filter(blur) is not applied, somehow filter blur prevents shift when modal opens */
    modalOpenStyle() {
      return {
        // topbar: this.modalOpen ? { 'padding-right': this.scrollbarWidth + 'px' } : {},
        blur: this.modalOpen || this.promptForPassphrase
      }
    },
    wrapperClass() {
      return { 'modal-open-blur': this.modalOpenStyle.blur, 'modal-passphrase': this.promptForPassphrase }
    },
    wrapperStyle() {
      return {
        'min-height': `${this.minHeight}px`
      }
    },
    ...mapState(['initialized', 'user', 'sideBarCollapsed'])
  },
  created() {
    this.$store.dispatch('setSideBarCollapsed', this.windowIsMobile())
    initializeFirebase(user => this.$store.dispatch('initialize', user))
    document.addEventListener('keydown', event => {
      if (event.ctrlKey && event.code === 'Space') {
        EventBus.$emit('activateSearch')
      }
      EventBus.$emit('keydown', event)
    })
    document.addEventListener('keydown', event => {
      if (event.ctrlKey && event.code === 'KeyM') this.$store.dispatch('setSideBarCollapsed', !this.sideBarCollapsed)
    })
    document.addEventListener('keydown', event => {
      if (event.ctrlKey && event.code === 'Period') this.$router.push({ name: 'rootNote' })
    })
    document.addEventListener('keydown', event => {
      if (event.ctrlKey && event.code === 'KeyQ') this.addNote()
    })
    const modals = ['signInModal', 'infoModal', 'accountModal', 'feedbackModal', 'deleteNoteModal']
    modals.forEach(modal => {
      const eventName = 'show' + modal.substr(0, 1).toUpperCase() + modal.substr(1)
      EventBus.$on(eventName, () => {
        this.modals = Object.assign({}, this.modals, { [modal]: true })
      })
    })
    window.addEventListener('resize', this.handleResize)
    this.handleResize()
  },
  mounted() {
    this.scrollbarWidth = getScrollbarWidth()
    this.windowWidth = window.innerWidth
    this.$router.beforeEach((to, from, next) => {
      window.scrollTo(0, 0)
      next()
    })
  },
  destroyed() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    windowIsMobile() {
      const breakpoint = parseInt(variables.sidebarCollapse.replace('px', ''))
      return window.innerWidth < breakpoint
    },
    async addNote() {
      const newNote = await this.$store.dispatch('addNote', { title: 'Untitled note' })
      this.goToNote(newNote.id)
    },
    handleResize: debounce(function () {
      const resizingHorizontally = window.innerWidth !== this.windowWidth
      this.windowWidth = window.innerWidth
      this.minHeight = window.innerHeight
      if (resizingHorizontally) this.$store.dispatch('setSideBarCollapsed', this.windowIsMobile())
    }, 100, { maxWait: 100 }),
    setTitle() {
      if (this.currentNote && this.initialized) document.title = `${this.currentNote.title} | Contexted`
    },
    closeModal(modal) {
      this.modals = Object.assign({}, this.modals, { [modal]: false })
    }
  },
  watch: {
    user() {
      // const currentRouteRequiresAuth = this.$route.meta.requiresAuth
      // if (currentRouteRequiresAuth && !this.user) this.$router.push({ name: 'rootNote' })
      if (!this.user) this.$router.push({ name: 'rootNote' })
    },
    currentNote() {
      this.setTitle()
    },
    initialized() {
      this.setTitle()
    }
  }
}

function getScrollbarWidth() {
  // Creating invisible container
  const outer = document.createElement('div')
  outer.style.visibility = 'hidden'
  outer.style.overflow = 'scroll' // forcing scrollbar to appear
  outer.style.msOverflowStyle = 'scrollbar' // needed for WinJS apps
  document.body.appendChild(outer)

  // Creating inner element and placing it in the container
  const inner = document.createElement('div')
  outer.appendChild(inner)

  // Calculating difference between container's full width and the child width
  const scrollbarWidth = (outer.offsetWidth - inner.offsetWidth)

  // Removing temporary elements from the DOM
  outer.parentNode.removeChild(outer)

  return scrollbarWidth
}
</script>
<style scoped lang="scss">
@import "~bootstrap/scss/mixins";
#main {
  position: relative;
  border-left: 1px solid $app-border-color;
  border-right: 1px solid $app-border-color;
  background-color: $white;
  margin-top: $topbar-height;
  padding: $spacer*0.5 $spacer*0.5;
  @include media-breakpoint-up(sm) {
    padding: $spacer*1.5 $spacer;
  }
  @include media-breakpoint-up(md) {
    padding-left: $spacer * 3;
    padding-right: $spacer * 3;
  }
}
#app {
  position: relative;
  // min-height: 100vh;
  max-width: $app-width;
  // #topbar,
  // #sidebar,
  // #main {
  //   transition: 0.5 * $transition-duration all ease-in;
  // }
}
#wrapper.modal-open-blur {
  #topbar,
  #sidebar,
  #main {
    filter: blur(5px);
  }
}
@include media-breakpoint-up(sm) {
  #sidebar + div {
    margin-left: map-get($sidebar-width, expanded);
    transition: margin $transition-duration ease-out;
  }
  #sidebar.collapsed + div {
    margin-left: 0;
  }
}
</style>
